import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { CurrentRouteConfig } from './../types';

@Injectable()
export class GuideNavigationService {
  private _baseUrl = '/guide';
  private _currentRouteConfig: CurrentRouteConfig[] = [];
  private _currentRouteConfigSubject = new BehaviorSubject<CurrentRouteConfig[]>(this._currentRouteConfig);
  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this._currentRouteConfig = this._initCurrentRouteConfig();
    this._currentRouteConfigSubject.next(this._currentRouteConfig);
  }

  /**
   * Getter for the base url of the guide.
   */
  get baseUrl(): string {
    return `${this._baseUrl}`;
  }

  /**
   * Returns an observable of the currentRouteConfig that is updated when the route changes.
   */
  get currentRouteConfig(): Observable<CurrentRouteConfig[]> {
    return this._currentRouteConfigSubject.asObservable();
  }

  /**
   * Returns the current route index.
   */
  public getCurrentRouteIndex(): number {
    return this._currentRouteConfig.findIndex((route) => route.active === true);
  }

  /**
   * returns true if the current route is the first route in the guide.
   */
  get isFirstRoute(): boolean {
    return this.getCurrentRouteIndex() === 0;
  }

  /**
   * Returns true if the current route is the last route in the guide.
   */
  get isLastRoute(): boolean {
    return this.getCurrentRouteIndex() === this._currentRouteConfig.length - 1;
  }

  /**
   * Navigate to the next route in the currentRouteConfig that is not active.  Set the current route to inactive and the next route to active.
   */
  public next(): void {
    // Get the current route index.
    const currentRouteIndex = this._currentRouteConfig.findIndex((route) => route.active === true);

    // Check if the next route index is greater than the length of the currentRouteConfig array.  If it is, set the router to navigate to '/team'.
    const nextRouteIndex = currentRouteIndex + 1;
    if (nextRouteIndex >= this._currentRouteConfig.length) {
      this.router.navigate(['/team']);
      return;
    }

    // Navigate to the next route.
    this.router.navigate([this._currentRouteConfig[nextRouteIndex].path], { relativeTo: this.route });

    // Set the current route to inactive.
    this._currentRouteConfig[currentRouteIndex].active = false;

    // Set the next route to active.
    this._currentRouteConfig[nextRouteIndex].active = true;

    this._currentRouteConfigSubject.next(this._currentRouteConfig);
  }

  /**
   * Navigate to the previous route in the currentRouteConfig that is not active.  Set the current route to inactive and the previous route to active.  If the route index is less than or equal to 0 navigate to the first index.
   */
  public previous(): void {
    // Get the current route index.
    const currentRouteIndex = this._currentRouteConfig.findIndex((route) => route.active === true);

    // Check if the previous route index is less than 0.  If it is, set the router to navigate to '/guide'.
    const previousRouteIndex = currentRouteIndex - 1;
    if (previousRouteIndex < 0) {
      this.router.navigate([this._baseUrl]);
      return;
    }

    // Navigate to the previous route.
    this.router.navigate([this._currentRouteConfig[previousRouteIndex].path], { relativeTo: this.route });

    // Set the current route to inactive.
    this._currentRouteConfig[currentRouteIndex].active = false;

    // Set the previous route to active.
    this._currentRouteConfig[previousRouteIndex].active = true;

    this._currentRouteConfigSubject.next(this._currentRouteConfig);
  }

  /**
   * Gets the path from the last route in the currentRouteConfig.
   */
  public getLastRoutePath(): string {
    if (this._currentRouteConfig.length > 0) {
      return this._currentRouteConfig[this._currentRouteConfig.length - 1].path || '';
    } else return '';
  }

  /**
   * Initialize the currentRouteConfig based on the activated route snapshot.
   */
  private _initCurrentRouteConfig(): CurrentRouteConfig[] {
    // Create an array of currentRouteConfig objects that exist in the activated routeconfig where the path is not empty.
    const currentRouteConfig: CurrentRouteConfig[] =
      this.route.routeConfig && this.route.routeConfig.children
        ? this.route.routeConfig.children
            .filter((route) => route.path !== '')
            .map((route) => {
              return {
                path: route.path,
                component: route.component,
                active: false,
              };
            })
        : [];

    // Set the first item to active.
    if (currentRouteConfig.length > 0) {
      currentRouteConfig[0].active = true;
    }

    return currentRouteConfig;
  }
}
